// Core Facet types derived from FACET-ADR-002 contracts and Moment ADR-001 MomentData IR

// ---------------------------------------------------------------------------
// Node & Connection Types — data-driven from topology, not fixed enums.
// The topology defines what types exist; Facet renders them generically.
// ---------------------------------------------------------------------------

export type NodeKind = string;

export type ConnectionStyle = string;

// ---------------------------------------------------------------------------
// Topology-provided visual metadata
// ---------------------------------------------------------------------------

export interface NodeTypeStyleDef {
  icon: string;
  fill: string;
  label: string;
  rx?: number;
  /** NDD palette role name (Miro legend vocabulary). */
  role?: string;
  /** Label ink on the fill: #1a1a1a, or white on the terminal banner. */
  ink?: string;
}

export interface ConnectionStyleDef {
  color: string;
  dash: boolean;
  alwaysVisible?: boolean;
}

// ---------------------------------------------------------------------------
// Schema Contract
// ---------------------------------------------------------------------------

export interface SchemaContractRef {
  contractId: string;
  requiredFields: string[];
  fieldTypes?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// MomentData IR (Moment ADR-001) + FACET extensions
// ---------------------------------------------------------------------------

export interface LaneDef {
  id: string;
  label: string;
  classification: string;
  color?: string;
  group?: 'context' | string;
  contextId?: string;
  isBranch?: boolean;
}

export interface NodeDef {
  id: string;
  lane: string;
  manifestRef?: string;
  label: string;
  type: NodeKind;
  detail?: string;
  adrs?: string[];
  aggregate?: string;
  optional?: boolean;
  terminal?: boolean;
  multiplicity?: string;
  path?: string;
  eventTypes?: string[];
}

export interface FrameDef {
  id: string;
  moment: string;
  isBranch: boolean;
  nodes: NodeDef[];
}

export interface ConnectionDef {
  /** Frame id (`moment-N-...`) in Moment-generated topologies. */
  from: string;
  /** Context id for boundary crossings; frame id for `return`-style edges. */
  to: string;
  label?: string;
  style: ConnectionStyle;
  crossBoundary?: boolean;
  relationship?: string;
  schemaContract?: SchemaContractRef;
  /**
   * Event type that traverses this connection (Moment artifact contract).
   * Old-format artifacts omit it — consumers must degrade to explicit
   * SKIPPED assertions rather than silently matching.
   */
  eventType?: string;
  /**
   * Branch condition this edge belongs to (condition→condition route
   * contract). When present, edge highlighting is condition-accurate:
   * the edge lights only when ITS branch point took THIS condition.
   */
  branchCondition?: string;
}

// ---------------------------------------------------------------------------
// Bounded Context & Context Map (DDD first-class vocabulary)
// ---------------------------------------------------------------------------

export type ContextRelationshipType =
  | 'partnership'
  | 'customer-supplier'
  | 'conformist'
  | 'anticorruption-layer'
  | 'open-host-service'
  | 'published-language'
  | 'separate-ways';

export interface BoundedContext {
  contextId: string;
  name: string;
  classification: ContextClassification;
  laneId: string;
  aggregates: string[];
  color?: string;
}

export interface ContextRelationship {
  sourceContextId: string;
  targetContextId: string;
  relationshipType: ContextRelationshipType;
  contractIds: string[];
}

export interface ContextMap {
  contexts: BoundedContext[];
  relationships: ContextRelationship[];
}

// ---------------------------------------------------------------------------
// Branch Predicates (FACET-ADR-002)
// ---------------------------------------------------------------------------

export interface BranchPredicate {
  field: string;
  defaultRoute: ConnectionStyle;
  routes: Record<string, ConnectionStyle>;
}

// ---------------------------------------------------------------------------
// Simulation Topology (= MomentData + FACET extensions)
// ---------------------------------------------------------------------------

export interface SimulationTopology {
  name?: string;
  flowId?: string;
  version?: number;
  lanes: LaneDef[];
  frames: FrameDef[];
  connections: ConnectionDef[];
  eventRouting: Record<string, string[]>;
  branchPredicates: Record<string, BranchPredicate>;
  adrLinks?: Record<string, string>;
  contextMap?: ContextMap;
  nodeTypeStyles?: Record<string, NodeTypeStyleDef>;
  connectionStyles?: Record<string, ConnectionStyleDef>;
}

// ---------------------------------------------------------------------------
// Platform Event (PADR-004 Envelope)
// ---------------------------------------------------------------------------
// Single source of truth lives in ComplaiEventEnvelope (src/contracts/event-envelope.ts).
// PlatformEvent is a re-export alias for backward compatibility.

import type { ComplaiEventEnvelope } from '../contracts/event-envelope.ts';

export type PlatformEvent = ComplaiEventEnvelope;

// ---------------------------------------------------------------------------
// Active Branch (ADR-031 §3 — branch decisions in Moment-generated scenarios)
// ---------------------------------------------------------------------------

export interface ActiveBranch {
  momentName: string;
  condition: string;
  routeChosen: string;
}

/** Type guard: is the value an ActiveBranch object (vs a plain string)? */
export function isActiveBranch(value: unknown): value is ActiveBranch {
  return typeof value === 'object' && value !== null && 'routeChosen' in value;
}

/**
 * Normalize activeBranches from either format to ActiveBranch[].
 * Legacy string[] entries are converted with the string as all three fields.
 */
export function normalizeBranches(branches: string[] | ActiveBranch[]): ActiveBranch[] {
  return branches.map((b) =>
    typeof b === 'string' ? { momentName: b, condition: b, routeChosen: b } : b,
  );
}

/** Extract route strings from either branch format. */
export function branchRoutes(branches: string[] | ActiveBranch[]): string[] {
  return branches.map((b) => (typeof b === 'string' ? b : b.routeChosen));
}

// ---------------------------------------------------------------------------
// Simulation Seed / Scenario
// ---------------------------------------------------------------------------

/**
 * Walk-derived scenario kind (upgraded Moment contract). Optional everywhere —
 * old artifacts omit it; consumers must feature-detect. 'timeout' marks
 * saga-timeout scenarios (label prefix 'Timeout:').
 */
export type ScenarioKind = 'happy' | 'variant' | 'failure' | 'negative' | 'timeout';

export interface SimulationSeed {
  scenarioId: string;
  flowId?: string;
  flowName?: string;
  /** Walk-derived scenario kind (upgraded artifacts only). */
  kind?: ScenarioKind;
  scenarioLabel: string;
  description: string;
  given?: string;
  when?: string;
  then?: string;
  expectedPath: string[];
  activeBranches: string[] | ActiveBranch[];
  events: PlatformEvent[];
}

/** Alias for Moment-generated scenario format (ADR-031 §2). */
export type SimulationScenario = SimulationSeed;

// ---------------------------------------------------------------------------
// Flow Manifest (MMNT-4480 — flow-structured manifest with per-flow topology)
// ---------------------------------------------------------------------------

export interface FlowManifest {
  flows: FlowEntry[];
  artifacts?: ManifestArtifacts;
}

export interface ManifestArtifacts {
  eventCatalog?: string;
  impactAnalysis?: string;
  sagaStateMachines?: string;
  /** Policy chain definitions (sibling of saga-state-machines.json). */
  policyDefinitions?: string;
  asyncApi?: string;
}

export interface FlowEntry {
  flowId: string;
  flowName: string;
  topology: string;
  scenarios: ManifestEntry[];
}

// ---------------------------------------------------------------------------
// Manifest Entry (ADR-031 — scenario index from manifest.json)
// ---------------------------------------------------------------------------

export interface ManifestEntry {
  scenarioId: string;
  scenarioLabel: string;
  description: string;
  file: string;
  eventCount: number;
  pathLength: number;
  branchCount: number;
  isHappyPath: boolean;
  isNegative: boolean;
  /** Walk-derived scenario kind (upgraded artifacts only; feature-detect). */
  kind?: ScenarioKind;
}

// ---------------------------------------------------------------------------
// Data Surface Catalog Types (Phase 2)
// ---------------------------------------------------------------------------

/** Which product stream produced the surface data — derived from PADR-004 envelope productSource */
export type ProductSource = string;

/** Classification of context within the domain model */
export type ContextClassification = 'core' | 'supporting' | 'generic' | 'external';

/** Browsing / filtering dimensions */
export type SurfaceBrowseMode = 'context-first' | 'shape-first' | 'action-first' | 'ownership';

/** Shape of data that a surface exposes */
export interface AvailableShape {
  shapeId: string;
  name: string;
  fields: ShapeField[];
  version: number;
}

export interface ShapeField {
  fieldName: string;
  fieldType: string;
  required: boolean;
  description?: string;
}

/** An action available on a surface */
export interface ActionSurface {
  actionId: string;
  name: string;
  inputShape?: string;
  outputShape?: string;
  description: string;
  commandId?: string;
}

/** Entry point for hydrating a surface from events */
export interface HydrationPoint {
  hydrationId: string;
  sourceEventTypes: string[];
  strategy: 'replay' | 'snapshot' | 'live';
  description: string;
}

/** Ownership metadata for a surface */
export interface DomainOwnership {
  contextId: string;
  contextName: string;
  classification: ContextClassification;
  ownerTeam?: string;
  steward?: string;
}

/** Freshness tracking for a surface */
export interface SurfaceFreshness {
  lastUpdatedAt: string;
  lastEventId: string;
  staleSince: string | null;
  isStale: boolean;
  ttlMs: number;
}

/** The main CatalogSurface aggregate — a data surface in the catalog */
export interface CatalogSurface {
  tenantId: string;
  surfaceId: string;
  name: string;
  description: string;
  productSource: ProductSource;
  ownership: DomainOwnership;
  shapes: AvailableShape[];
  actions: ActionSurface[];
  hydrationPoints: HydrationPoint[];
  freshness: SurfaceFreshness;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

/** Key for indexing surfaces in the store */
export interface SurfaceKey {
  tenantId: string;
  surfaceId: string;
}

/** Visualization category for enriched events */
export type VisualizationCategory = 'catalog' | 'simulation' | 'schema' | 'overlay';

/** Enriched event — a platform event with spatial/temporal/viz metadata */
export interface EnrichedEvent {
  event: PlatformEvent;
  spatialCoordinates: {
    contextId: string;
    aggregateId?: string;
    laneId?: string;
    nodeId?: string;
  };
  temporalPosition: {
    sequenceNumber: number;
    wallClock: string;
    causalDepth: number;
  };
  visualizationCategory: VisualizationCategory;
}

/** Connection status for offline resilience */
export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

// ---------------------------------------------------------------------------
// Schema Surface Types (Phase 3)
// ---------------------------------------------------------------------------

/** Evolution status of a schema element */
export type SchemaElementStatus = 'active' | 'deprecated' | 'removed' | 'new';

/** Kind of schema element in the supergraph */
export type SchemaElementKind =
  | 'type'
  | 'field'
  | 'query'
  | 'mutation'
  | 'subscription'
  | 'enum'
  | 'union'
  | 'interface'
  | 'input'
  | 'scalar'
  | 'directive';

/** A single element in the schema (type, field, query, mutation, etc.) */
export interface SchemaElement {
  elementId: string;
  kind: SchemaElementKind;
  name: string;
  parentElementId?: string;
  subgraphId: string;
  description: string;
  status: SchemaElementStatus;
  deprecatedSince?: string;
  deprecationReason?: string;
  removalTarget?: string;
  isBreakingChange: boolean;
  consumerImpact?: string[];
  domainMapping?: DomainToSchemaMapping;
}

/** Subgraph registration in the federated supergraph */
export interface SubgraphRegistration {
  subgraphId: string;
  name: string;
  contextId: string;
  url?: string;
  registeredAt: string;
  version: number;
  elementIds: string[];
}

/** Bidirectional mapping between a schema element and a domain concept */
export interface DomainToSchemaMapping {
  schemaElementId: string;
  domainConceptType: 'aggregate' | 'valueObject' | 'event' | 'command' | 'projection' | 'invariant';
  domainConceptId: string;
  domainConceptName: string;
  contextId: string;
  direction: 'schema-to-domain' | 'domain-to-schema' | 'bidirectional';
}

/** Schema evolution record */
export interface SchemaEvolution {
  evolutionId: string;
  schemaMapId: string;
  timestamp: string;
  changeType: 'addition' | 'deprecation' | 'removal' | 'breaking' | 'modification';
  affectedElementIds: string[];
  description: string;
  consumerImpact: string[];
}

/** The SchemaMap aggregate — supergraph organized by bounded context */
export interface SchemaMap {
  tenantId: string;
  schemaMapId: string;
  name: string;
  subgraphs: Record<string, SubgraphRegistration>;
  elements: Record<string, SchemaElement>;
  mappings: DomainToSchemaMapping[];
  evolutions: SchemaEvolution[];
  version: number;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Cross-Pillar Integration Types (Phase 4)
// ---------------------------------------------------------------------------

/** Zoom level for semantic zoom */
export type ZoomLevel = 'ecosystem' | 'context' | 'element';

/** Canvas overlay layers */
export type OverlayLayer = 'simulation' | 'catalog' | 'schema';

/** Entitlement tier */
export type EntitlementTier = 'free' | 'paid';

/** Canvas interaction event types (productSource: "facet") */
export type CanvasInteractionType =
  | 'CanvasNavigated'
  | 'SimulationReplayed'
  | 'SurfaceExplored'
  | 'OverlayToggled'
  | 'SchemaNavigated';

/** CanvasSession — tracks user interaction with the canvas */
export interface CanvasSession {
  sessionId: string;
  tenantId: string;
  startedAt: string;
  zoomLevel: ZoomLevel;
  activeOverlays: OverlayLayer[];
  interactionCount: number;
  lastInteractionAt: string;
}

/** Account entitlement for feature gating */
export interface AccountEntitlement {
  tenantId: string;
  tier: EntitlementTier;
  maxContexts: number;
  maxScenarios: number;
  overlaysEnabled: boolean;
  schemaNavigationEnabled: boolean;
}
